import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Progress } from '@/shared/components/ui/progress';
import {
  Megaphone,
  Plus,
  Search,
  DollarSign,
  Eye,
  MousePointer,
  Target,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Settings,
  Play,
  Pause,
  AlertCircle,
  CheckCircle } from
'lucide-react';

// Mock ad campaigns
const mockAdCampaigns = [
{
  id: 1,
  name: 'Black Friday Promo',
  platform: 'meta_ads',
  status: 'active',
  budget: 5000,
  spent: 3245,
  impressions: 450000,
  clicks: 12500,
  conversions: 234,
  ctr: 2.78,
  cpc: 0.26,
  roas: 3.2
},
{
  id: 2,
  name: 'Brand Awareness',
  platform: 'google_ads',
  status: 'active',
  budget: 3000,
  spent: 1890,
  impressions: 280000,
  clicks: 5600,
  conversions: 89,
  ctr: 2.0,
  cpc: 0.34,
  roas: 2.8
},
{
  id: 3,
  name: 'Retargeting',
  platform: 'meta_ads',
  status: 'paused',
  budget: 2000,
  spent: 1200,
  impressions: 120000,
  clicks: 4800,
  conversions: 156,
  ctr: 4.0,
  cpc: 0.25,
  roas: 4.5
}];


const budgetRecommendations = [
{ channel: 'Meta Ads', current: 5000, recommended: 6500, change: 30, reason: 'High ROAS performance' },
{ channel: 'Google Ads', current: 3000, recommended: 3500, change: 17, reason: 'Good conversion rate' },
{ channel: 'LinkedIn Ads', current: 1000, recommended: 800, change: -20, reason: 'Lower engagement' }];


export const AdsManager = () => {const { t } = useTranslation("cns");
  const [activeTab, setActiveTab] = useState('campaigns');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Calculate totals
  const totalBudget = mockAdCampaigns.reduce((sum, c) => sum + c.budget, 0);
  const totalSpent = mockAdCampaigns.reduce((sum, c) => sum + c.spent, 0);
  const totalImpressions = mockAdCampaigns.reduce((sum, c) => sum + c.impressions, 0);
  const totalConversions = mockAdCampaigns.reduce((sum, c) => sum + c.conversions, 0);

  return (
    <div className="space-y-6" data-testid="ads-manager">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <Megaphone className="h-7 w-7 text-white" />
            </div>{t("adsManager", "Ads Manager")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("managePaidAdvertising", "Manage paid advertising across Meta, Google, and more")}

          </p>
        </div>
        <Button className="gap-2 bg-gradient-to-r from-amber-500 to-orange-600">
          <Plus className="h-4 w-4" />{t("newAdCampaign", "New Ad Campaign")}

        </Button>
      </div>

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalBudget", "Total Budget")}</p>
                <p className="text-2xl font-bold">{formatCurrency(totalBudget)}</p>
                <Progress value={totalSpent / totalBudget * 100} className="mt-2 h-2" />
                <p className="text-xs text-muted-foreground mt-1">{formatCurrency(totalSpent)}{t("spent", "spent")}</p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("impressions", "Impressions")}</p>
                <p className="text-2xl font-bold">{formatNumber(totalImpressions)}</p>
                <p className="text-xs text-emerald-600 flex items-center mt-1">
                  <ArrowUpRight className="h-3 w-3" />{t("125VsLast", "+12.5% vs last period")}
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("conversions", "Conversions")}</p>
                <p className="text-2xl font-bold">{totalConversions}</p>
                <p className="text-xs text-emerald-600 flex items-center mt-1">
                  <ArrowUpRight className="h-3 w-3" />{t("83VsLast", "+8.3% vs last period")}
                </p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Target className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgRoas", "Avg. ROAS")}</p>
                <p className="text-2xl font-bold">{t("35x", "3.5x")}</p>
                <p className="text-xs text-emerald-600 flex items-center mt-1">
                  <ArrowUpRight className="h-3 w-3" />{t("03xVsLast", "+0.3x vs last period")}
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="campaigns" className="gap-2">
            <Megaphone className="h-4 w-4" />{t("campaigns", "Campaigns")}
          </TabsTrigger>
          <TabsTrigger value="budget" className="gap-2">
            <DollarSign className="h-4 w-4" />{t("budgetControl", "Budget Control")}
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2">
            <BarChart3 className="h-4 w-4" />{t("performance", "Performance")}
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4 mt-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search campaigns..." className="pl-10" />
          </div>

          <div className="space-y-3">
            {mockAdCampaigns.map((campaign) =>
            <Card key={campaign.id} className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Campaign Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={campaign.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {campaign.status === 'active' ? <Play className="h-3 w-3 mr-1" /> : <Pause className="h-3 w-3 mr-1" />}
                          {campaign.status}
                        </Badge>
                        <Badge variant="outline">{campaign.platform.replace('_', ' ')}</Badge>
                      </div>
                      <h3 className="text-lg font-semibold">{campaign.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-muted-foreground">{t("budget", "Budget:")}</span>
                        <span className="text-sm font-medium">{formatCurrency(campaign.budget)}</span>
                        <Progress value={campaign.spent / campaign.budget * 100} className="w-20 h-2" />
                        <span className="text-xs text-muted-foreground">{(campaign.spent / campaign.budget * 100).toFixed(0)}%</span>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6">
                      <div className="text-center">
                        <p className="text-lg font-bold">{formatNumber(campaign.impressions)}</p>
                        <p className="text-xs text-muted-foreground">{t("impressions", "Impressions")}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold">{campaign.ctr}%</p>
                        <p className="text-xs text-muted-foreground">{t("ctr", "CTR")}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold">{campaign.conversions}</p>
                        <p className="text-xs text-muted-foreground">{t("conversions", "Conversions")}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-emerald-600">{campaign.roas}x</p>
                        <p className="text-xs text-muted-foreground">{t("roas", "ROAS")}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">{t("edit", "Edit")}</Button>
                      <Button variant="outline" size="icon" className="h-8 w-8">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Budget Control Tab */}
        <TabsContent value="budget" className="space-y-4 mt-4">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-amber-600" />{t("budgetRecommendations", "Budget Recommendations")}

              </CardTitle>
              <CardDescription>{t("aipoweredSuggestionsBased", "AI-powered suggestions based on campaign performance")}

              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {budgetRecommendations.map((rec, index) =>
                <div key={index} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${rec.change > 0 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                        {rec.change > 0 ?
                      <ArrowUpRight className={`h-5 w-5 ${rec.change > 0 ? 'text-emerald-600' : 'text-amber-600'}`} /> :

                      <ArrowDownRight className="h-5 w-5 text-amber-600" />
                      }
                      </div>
                      <div>
                        <p className="font-semibold">{rec.channel}</p>
                        <p className="text-sm text-muted-foreground">{rec.reason}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{formatCurrency(rec.current)}</span>
                        <span>→</span>
                        <span className="font-bold">{formatCurrency(rec.recommended)}</span>
                      </div>
                      <Badge variant="outline" className={rec.change > 0 ? 'text-emerald-600' : 'text-amber-600'}>
                        {rec.change > 0 ? '+' : ''}{rec.change}%
                      </Badge>
                    </div>
                    <Button size="sm">{t("apply", "Apply")}</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("budgetAlerts", "Budget Alerts")}</CardTitle>
              <CardDescription>{t("configureSpendingAlerts", "Configure spending alerts and limits")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium">{t("dailySpendLimit", "Daily spend limit approaching")}</p>
                      <p className="text-sm text-muted-foreground">{t("metaAds85", "Meta Ads - 85% of daily budget used")}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline">{t("adjust", "Adjust")}</Button>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-emerald-200 bg-emerald-50">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="font-medium">{t("monthlyBudgetOn", "Monthly budget on track")}</p>
                      <p className="text-sm text-muted-foreground">{t("63SpentWith", "63% spent with 65% of month elapsed")}</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">{t("healthy", "Healthy")}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4 mt-4">
          <Card className="card-elevated border-dashed">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                <BarChart3 className="h-10 w-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t("performanceAnalytics", "Performance Analytics")}</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("detailedChartsAnd", "Detailed charts and reports will be displayed here once connected to real ad platforms.")}

              </p>
              <Button variant="outline">{t("configureIntegrations", "Configure Integrations")}</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};

export default AdsManager;